/**
 * CarTrail — pure ring buffer of displayed poses backing the Ignition Red
 * comet-tail trail behind the driving car (PLA-273, ADR-0030 §8).
 *
 * The trail traces the last ~2.5 s of the DISPLAYED spline path (it bends
 * through corners), never the raw GPS fixes — callers push the per-tick pose
 * the CarMotionEngine emits, not Pusher payloads:
 *
 * - `pushTrailPoint` appends a timestamped pose and evicts points older than
 *   `maxAgeMs`. A speed gate drops pushes below `minSpeedMps`: a (nearly)
 *   parked car stops feeding the buffer, so the existing tail ages out and
 *   the trail disappears. Gated pushes still evict — the buffer drains
 *   structurally, not just visually.
 * - `trailSegments` turns the buffer into strokeable from→to pairs whose
 *   alpha decays with age: the newest segment sits at `maxAlpha` (~0.55) and
 *   fades to 0 at `maxAgeMs`. Segments are emitted oldest → newest so a
 *   painter's-order renderer keeps the bright head on top.
 *
 * This module is intentionally pure TypeScript: no DOM, no google.maps, no
 * React — and it NEVER reads the clock. Callers pass `timestampMs` / `nowMs`
 * from one consistent time source (the same tick clock the motion engine
 * runs on, see SmoothCarMarker).
 */

/** Tunable behavior for the trail buffer. */
export interface CarTrailConfig {
  /** Points older than this are evicted / rendered fully transparent. */
  maxAgeMs: number
  /**
   * Speed gate (meters per second). Pushes below this displayed speed are
   * dropped so the tail drains while (nearly) parked. Matches the sprite
   * engine's heading speed gate — one "the car is moving" threshold.
   */
  minSpeedMps: number
  /** Alpha of the newest segment; older segments decay linearly to 0. */
  maxAlpha: number
  /** Hard cap on buffered points (safety net beyond age eviction). */
  maxPoints: number
}

export const DEFAULT_CAR_TRAIL_CONFIG: CarTrailConfig = {
  maxAgeMs: 2500,
  minSpeedMps: 1,
  maxAlpha: 0.55,
  maxPoints: 256,
}

/** One buffered displayed pose. */
export interface CarTrailPoint {
  lat: number
  lng: number
  timestampMs: number
}

/** A displayed pose offered to the buffer (speed decides acceptance). */
export interface CarTrailInput {
  lat: number
  lng: number
  /** Tick-clock time of this pose (same clock as `trailSegments`' nowMs). */
  timestampMs: number
  /** Displayed ground speed of the pose (the motion engine's speedMps). */
  speedMps: number
}

/** Trail state. Treat as opaque; create via `createTrailState`. */
export interface CarTrailState {
  /** Buffered points, oldest first. */
  points: readonly CarTrailPoint[]
}

/** One strokeable slice of the tail. */
export interface CarTrailSegment {
  from: { lat: number; lng: number }
  to: { lat: number; lng: number }
  /** Stroke opacity in (0, maxAlpha], decaying with the segment's age. */
  alpha: number
}

/** Creates the initial (empty) trail state. */
export function createTrailState(): CarTrailState {
  return { points: [] }
}

/**
 * Offers a displayed pose to the buffer.
 *
 * Pure: returns a NEW state (or the same reference when nothing changed);
 * never mutates inputs.
 *
 * Behavior:
 * - Never ingests non-finite coordinates/timestamps (WEB-1Q NaN guard,
 *   mirrored at buffer level like the motion engine's).
 * - Always evicts points older than `maxAgeMs` relative to the incoming
 *   timestamp — including on speed-gated pushes, so a parked car's buffer
 *   empties instead of holding the stale tail forever.
 * - Appends only when `speedMps` is finite and ≥ `minSpeedMps`.
 */
export function pushTrailPoint(
  state: CarTrailState,
  input: CarTrailInput,
  config: Partial<CarTrailConfig> = {},
): CarTrailState {
  const cfg: CarTrailConfig = { ...DEFAULT_CAR_TRAIL_CONFIG, ...config }
  if (
    !Number.isFinite(input.lat) ||
    !Number.isFinite(input.lng) ||
    !Number.isFinite(input.timestampMs)
  ) {
    return state
  }

  const cutoffMs = input.timestampMs - cfg.maxAgeMs
  let points = state.points.filter((point) => point.timestampMs > cutoffMs)

  const gated = !Number.isFinite(input.speedMps) || input.speedMps < cfg.minSpeedMps
  if (gated) {
    // Nothing evicted either → the state is unchanged; keep the reference.
    return points.length === state.points.length ? state : { points }
  }

  points = [...points, { lat: input.lat, lng: input.lng, timestampMs: input.timestampMs }]
  if (points.length > cfg.maxPoints) {
    points = points.slice(points.length - cfg.maxPoints)
  }
  return { points }
}

/**
 * Resolves the strokeable tail at `nowMs`: consecutive point pairs, oldest
 * first, each carrying an age-decayed alpha (newest ≈ `maxAlpha`, → 0 at
 * `maxAgeMs`). Fully-faded and zero-length pairs are skipped — round line
 * caps would render a zero-length segment as a stray dot.
 */
export function trailSegments(
  state: CarTrailState,
  nowMs: number,
  config: Partial<CarTrailConfig> = {},
): CarTrailSegment[] {
  const cfg: CarTrailConfig = { ...DEFAULT_CAR_TRAIL_CONFIG, ...config }
  const segments: CarTrailSegment[] = []

  for (let i = 1; i < state.points.length; i++) {
    const from = state.points[i - 1]
    const to = state.points[i]
    if (!from || !to) continue
    if (from.lat === to.lat && from.lng === to.lng) continue

    // Age of the segment's NEWER endpoint drives the fade; clamp guards a
    // pose pushed within the same millisecond as the render tick.
    const ageFraction = Math.min(Math.max((nowMs - to.timestampMs) / cfg.maxAgeMs, 0), 1)
    const alpha = cfg.maxAlpha * (1 - ageFraction)
    if (alpha <= 0) continue

    segments.push({
      from: { lat: from.lat, lng: from.lng },
      to: { lat: to.lat, lng: to.lng },
      alpha,
    })
  }
  return segments
}
